import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getActiveLanguage } from "react-localize-redux";
import { useSelector } from "react-redux";

import { MainContext } from "@context";

const selectBanners = (state) => ({
	isLoading: state.cms.loading,
	banners: state.cms.containerData,
	currentLanguage: getActiveLanguage(state.localize).code,
});

const useController = ({ history }) => {
	const bannerStylesRef = useRef(null);

	const { banners, isLoading, currentLanguage } = useSelector(selectBanners);
	const {
		state: { isMobile, isTablet, isDesktop, isWide, isUltraWide },
	} = useContext(MainContext);

	const [filteredBanners, setFilteredBanners] = useState([]);

	const getBannerImage = (banner) => {
		if (isMobile && banner.mobile) return banner.mobile;
		if (isTablet && banner.tablet) return banner.tablet;
		if (isWide && banner.wide) return banner.wide;
		if (isDesktop && banner.desktop) return banner.desktop;
		if (isUltraWide && banner.wide) return banner.wide;
		return banner.desktop || banner.mobile;
	};

	const getPromotionLink = (banner) => {
		const titleParam = banner.urlPath || banner.title;
		return `/bonus?title=${encodeURIComponent(titleParam)}`;
	};

	const getPromotionCount = useCallback(() => {
		return window.location.search === "?page=casino"
			? window.CASINO_PROMOTION_COUNT
			: window.LIVE_CASINO_PROMOTION_COUNT;
	}, []);

	const handleHoverPromotion = (e) => {
		e.preventDefault();

		let position = [e.offsetX, e.offsetY];
		if (e.type === "touchmove") {
			position = [e.touches[0].clientX, e.touches[0].clientY];
			bannerStylesRef.current.innerHTML = "";
		}
		const $card = e.target;

		const l = position[0];
		const t = position[1];
		const cardPos = $card.getBoundingClientRect();
		const h = cardPos.height;
		const w = cardPos.width;
		const px = Math.abs(Math.floor((100 / w) * l) - 100);
		const py = Math.abs(Math.floor((100 / h) * t) - 100);
		const lp = 50 + (px - 50) / 1.5;
		const tp = 50 + (py - 50) / 1.5;

		const grad_pos = `background-position: ${lp}% ${tp}% !important;`;
		const style = `
      .promotion-item:hover:before { ${grad_pos} }  /* gradient */
    `;

		bannerStylesRef.current.innerHTML = style;

		if (e.type === "touchmove") {
			return false;
		}
	};

	const handleLeavePromotion = () => {
		bannerStylesRef.current.innerHTML = "";
	};

	const handlePromotionClick = useCallback(
		(banner) => {
			const link = getPromotionLink(banner);
			history.push(link);
		},
		[history, getPromotionLink]
	);

	useEffect(() => {
		if (banners) {
			const promotionBanner = banners.find((banner) => {
				if (window.location.search === "?page=casino") {
					return banner.name === "CasinoPromotionBanner";
				} else {
					return banner.name === "LiveCasinoPromotionBanner";
				}
			});

			const hasActiveBanner =
				promotionBanner &&
				promotionBanner.banners.filter(
					({ language, status }) => language === currentLanguage && status !== "passive"
				).length > 0;

			if (!hasActiveBanner) return;

			const filteredBanners = promotionBanner.banners
				.filter(
					({ language, status }) => language === currentLanguage && status !== "passive"
				)
				.sort((a, b) => (a.order > b.order ? 1 : -1));

			setFilteredBanners(filteredBanners);
		}
	}, [banners, currentLanguage]);

	useEffect(() => {
		if (!filteredBanners?.length) return;

		const promotions = document.querySelectorAll(".promotion-item");

		promotions.forEach((p) => {
			p.addEventListener("mousemove", handleHoverPromotion);
			p.addEventListener("mouseleave", handleLeavePromotion);
			p.addEventListener("touchmove", handleHoverPromotion);
			p.addEventListener("touchend", handleLeavePromotion);
		});

		return () => {
			const promotions = document.querySelectorAll(".promotion-item");

			promotions.forEach((p) => {
				p.removeEventListener("mousemove", handleHoverPromotion);
				p.removeEventListener("mouseleave", handleLeavePromotion);
				p.removeEventListener("touchmove", handleHoverPromotion);
				p.removeEventListener("touchend", handleLeavePromotion);
			});
		};
	}, [filteredBanners]);

	return {
		refs: {
			bannerStylesRef,
		},
		helpers: {
			getBannerImage,
			getPromotionLink,
		},
		state: {
			isLoading: isLoading,
			banners: filteredBanners,
			promotionCount: getPromotionCount(),
		},
		handlers: {
			handlePromotionClick,
		},
	};
};

export default useController;
